import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = config.apiUrl;

export type JudgeRoleId = "judge1" | "judge2" | "judge3";

export type JudgeDisplayNames = {
  judge1: string | null;
  judge2: string | null;
  judge3: string | null;
};

export type EventJudgeLoginListItem = {
  id: string;
  name: string;
  slug: string;
  status: string;
  venue: string | null;
  eventDate: string | null;
  isOpen: boolean;
  resultsShown: boolean;
  createdAt: string;
  updatedAt: string;
  judgeDisplayNames: JudgeDisplayNames;
};

export type JudgeAccessResponse =
  | { kind: "admin" }
  | { kind: "judge"; role: JudgeRoleId };

/** Admin list/detail row (PIN hashes stripped server-side). */
export type AdminEventRow = {
  id: string;
  name: string;
  slug: string;
  status: string;
  description: string | null;
  venue: string | null;
  eventDate: string | null;
  isOpen: boolean;
  resultsShown: boolean;
  createdAt: string;
  updatedAt: string;
  judgeDisplayNames: JudgeDisplayNames;
};

export type EventJudgeProfileRow = {
  role: JudgeRoleId;
  displayName: string | null;
  notes: string | null;
};

export type UpsertJudgeProfilePayload = {
  displayName?: string | null;
  notes?: string | null;
};

export type CreateEventPayload = {
  name: string;
  slug: string;
  description?: string;
  venue?: string;
  eventDate?: string | null;
  judge1Pin?: string;
  judge2Pin?: string;
  judge3Pin?: string;
};

export type UpdateEventPayload = {
  name?: string;
  slug?: string;
  description?: string | null;
  venue?: string | null;
  eventDate?: string | null;
  status?: "draft" | "scoring" | "closed" | "published";
  judge1Pin?: string;
  judge2Pin?: string;
  judge3Pin?: string;
};

async function fetchEventRow(eventId: string): Promise<AdminEventRow> {
  return fetcher.get<AdminEventRow>(`${base}/events/${eventId}`);
}

export const eventsApi = {
  /** Active events judges can choose (status scoring | closed); public endpoint */
  listPublicEvents(): Promise<EventJudgeLoginListItem[]> {
    return fetcher.get<EventJudgeLoginListItem[]>(
      `${base}/events/judge-login-list`,
    );
  },

  getJudgeAccess(eventId: string): Promise<JudgeAccessResponse> {
    return fetcher.get<JudgeAccessResponse>(
      `${base}/events/${eventId}/me/judge-access`,
    );
  },

  listAdminEvents(): Promise<AdminEventRow[]> {
    return fetcher.get<AdminEventRow[]>(`${base}/events`);
  },

  /** GET /events/:id — omit PIN hashes; judges & public display. */
  getEvent: fetchEventRow,

  getAdminEvent: fetchEventRow,

  createAdminEvent(body: CreateEventPayload): Promise<AdminEventRow> {
    return fetcher.post<AdminEventRow>(`${base}/events`, body);
  },

  updateAdminEvent(
    eventId: string,
    body: UpdateEventPayload,
  ): Promise<AdminEventRow> {
    return fetcher.patch<AdminEventRow>(`${base}/events/${eventId}`, body);
  },

  deleteAdminEvent(eventId: string): Promise<AdminEventRow> {
    return fetcher.delete<AdminEventRow>(`${base}/events/${eventId}`);
  },

  listJudgeProfiles(eventId: string): Promise<EventJudgeProfileRow[]> {
    return fetcher.get<EventJudgeProfileRow[]>(
      `${base}/events/${eventId}/judge-profiles`,
    );
  },

  patchJudgeProfile(
    eventId: string,
    role: JudgeRoleId,
    body: UpsertJudgeProfilePayload,
  ): Promise<EventJudgeProfileRow> {
    return fetcher.patch<EventJudgeProfileRow>(
      `${base}/events/${eventId}/judge-profiles/${role}`,
      body,
    );
  },
};
